import React, { useState } from 'react';
import axios from 'axios';
import './QuoteRequestModal.css';

const QuoteRequestModal = ({ isOpen, onClose, configuration, pricing }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    addressType: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteId, setQuoteId] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post('/api/quotes/submit', {
        configuration,
        customerInfo: formData,
        totalPrice: pricing?.totalPrice,
        parts: pricing?.parts
      });

      if (response.data.success) {
        setSubmitted(true);
        setQuoteId(response.data.quoteId);
        try {
          window.parent.postMessage({
            type: 'configurator-quote-submitted',
            quoteId: response.data.quoteId,
            value: pricing?.totalPrice ?? 0,
            currency: 'USD',
          }, '*');
        } catch (e) { /* no-op */ }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      addressType: '',
      notes: ''
    });
    setSubmitted(false);
    setQuoteId('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={handleClose}>
      <div className="quote-modal-content" onClick={(e) => e.stopPropagation()}>
        {!submitted ? (
          <>
            <div className="quote-modal-header">
              <h2>Request a Quote</h2>
              <button className="close-button" onClick={handleClose}>×</button>
            </div>

            <div className="quote-modal-body">
              <p className="quote-intro">
                Please provide your contact information and we'll get back to you with a custom quote for your configured workbench.
              </p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="quote-form">
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="name">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="company">
                      Company Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Acme Corporation"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="phone">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="addressType">
                    Select Address Type <span className="required">*</span>
                  </label>
                  <select
                    id="addressType"
                    name="addressType"
                    value={formData.addressType}
                    onChange={handleChange}
                    required
                    className="address-type-select"
                  >
                    <option value="">Address Type *</option>
                    <option value="commercial-dock">Commercial address with a loading dock</option>
                    <option value="commercial-no-dock">Commercial address WITHOUT a loading dock ($199.00)</option>
                    <option value="home-delivery">Home Delivery ($199.00)</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="notes">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any special requirements or questions..."
                  />
                </div>

                <div className="quote-summary">
                  <h3>Your Configuration</h3>
                  <div className="parts-summary">
                    {pricing?.parts?.map((part, index) => (
                      <div key={index} className="part-item">
                        <span>{part.stepName}: {part.optionName}</span>
                        {part.price > 0 && <span>+${part.price.toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                  {pricing?.totalPrice && (
                    <div className="estimated-total">
                      Estimated Total: ${pricing.totalPrice.toFixed(2)}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Quote Request'}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          <>
            <div className="quote-modal-header">
              <h2>✓ Quote Request Submitted!</h2>
              <button className="close-button" onClick={handleClose}>×</button>
            </div>

            <div className="quote-modal-body">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank you for your quote request!</h3>
                <p>
                  Your quote request has been received and our team will review it shortly.
                </p>
                <div className="quote-id-box">
                  <strong>Quote ID:</strong> {quoteId}
                </div>
                <p className="follow-up">
                  We'll contact you at <strong>{formData.email}</strong> within 1-2 business days with your custom quote.
                </p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteRequestModal;
